import { useEffect, useRef } from "react";

// Fixed, site-wide starfield + drifting particles. Static when reduced motion is preferred.
export default function StarField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let w, h, raf, stars = [];

    const build = () => {
      w = window.innerWidth; h = window.innerHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(160, Math.round((w * h) / 9000));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        r: Math.random() * 1.1 + 0.2, a: Math.random() * 0.6 + 0.15,
        t: Math.random() * Math.PI * 2, s: Math.random() * 0.012 + 0.004,
        dy: -(Math.random() * 0.08 + 0.01), cyan: Math.random() < 0.25,
      }));
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const st of stars) {
        if (!reduce) {
          st.t += st.s; st.y += st.dy;
          if (st.y < -2) { st.y = h + 2; st.x = Math.random() * w; }
        }
        const alpha = st.a * (0.6 + 0.4 * Math.sin(st.t));
        ctx.beginPath();
        ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2);
        ctx.fillStyle = st.cyan ? `rgba(110,231,249,${alpha})` : `rgba(200,225,255,${alpha})`;
        ctx.fill();
      }
      if (!reduce && !document.hidden) raf = requestAnimationFrame(draw);
    };
    const onVis = () => { if (!document.hidden && !reduce) { cancelAnimationFrame(raf); draw(); } };
    const onResize = () => { build(); if (reduce) draw(); };

    build(); draw();
    window.addEventListener("resize", onResize);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return <canvas ref={ref} className="fixed inset-0 w-full h-full pointer-events-none z-0" aria-hidden="true" data-testid="starfield" />;
}
